import routes from '@Constants/Routes'
import { Icon, Icons, NavigationBar } from '@eduact/ed-components'
import Spacer from '@eduact/ed-components/Spacer'
import { FlexLayout, Typography } from '@eduact/ed-system'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { rgba } from 'polished'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Outlet, useNavigate } from 'react-router'
import styled from 'styled-components'
import { NavigationLayoutProps } from './DashboardLayout.types'
import UploadProfilePhoto from './UploadProfilePhoto'

const ResponsiveLayout: React.VoidFunctionComponent<NavigationLayoutProps> = ({ currentTab }) => {
	const [t] = useTranslation("studentDashboard")
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { classrooms_enabled, standalone_courses_enabled } = useAppSelector(s => s.settings)
	const handleOnChange = (value: string | number) => {
		if (pathname.endsWith(value.toString())) return
		navigate(`${routes.dashboard}${value}`)
	}
	return (
		<Wrapper>
			<UploadProfilePhoto />
			<FlexLayout minHeight={'90vh'} flex={1}>
				<Outlet />
			</FlexLayout>
			{/*
			 * Built as a filtered array rather than inline `{flag && <Item/>}`.
			 *
			 * NavigationBar maps its children with React.Children.map and reads
			 * child.props on each one. React.Children.map does NOT skip booleans —
			 * it invokes the callback with null — so a single disabled tab made the
			 * whole dashboard throw "Cannot read properties of null (reading
			 * 'props')" into the app boundary. This bar only renders on
			 * mobile/tablet, which is why the dashboard died there and nowhere else.
			 */}
			<FixedNavigationBar onChange={(value: any) => handleOnChange(value as string)} value={currentTab ?? ''}>
				{[
					<NavigationBar.Item
						key="overview"
						icon={
							<Icon>
								<Icons.Dashboard />
							</Icon>
						}
						value="/"
						label={t('overview')}
					/>,
					<NavigationBar.Item
						key="profile"
						icon={
							<Icon>
								<Icons.User />
							</Icon>
						}
						value="/profile"
						label={t('profile')}
					/>,
					classrooms_enabled && (
						<NavigationBar.Item
							key="classrooms"
							icon={
								<Icon>
									<Icons.ClassBookmark />
								</Icon>
							}
							value="/classrooms"
							label={t('classrooms')}
						/>
					),
					standalone_courses_enabled && (
						<NavigationBar.Item
							key="courses"
							icon={
								<Icon>
									<Icons.ClassBookmark />
								</Icon>
							}
							value="/courses"
							label={t('courses')}
						/>
					),
					<NavigationBar.Item
						key="activity"
						icon={
							<Icon>
								<Icons.Activity />
							</Icon>
						}
						value="/activity"
						label={t('activity')}
					/>,
					<NavigationBar.Item
						key="settings"
						icon={
							<Icon>
								<Icons.Settings />
							</Icon>
						}
						value="/settings"
						label={t('settings')}
					/>,
				].filter(Boolean)}
			</FixedNavigationBar>
		</Wrapper>
	)
}

const FixedNavigationBar = styled(NavigationBar)`
	width: 100%;
	height: fit-content;
	position: sticky;
	bottom: 0;
	z-index: 999;
`
const IconStyle = styled(Icon)`
	color: #000;
`
export const Wrapper = styled.div`
	width: 100%;
	background-color: ${rgba('#ecebfc', 0.7)};
	padding: 11px 9px;
	padding-bottom: 0;
	${({ theme }) => `${theme.mediaQueries.large}{
		display: none;
		height: inherit;
		-webkit-box-align: stretch;
		align-items: stretch;
	}`}
`
export const Container = styled.div`
	width: 100%;
	background: #fff;
	border-top-left-radius: 15px;
	border-top-right-radius: 15px;
	box-shadow: none;
	transition: width 1s cubic-bezier(0.68, -0.55, 0.27, 1.55) 0s;
	position: absolute;
	bottom: 0;
	box-shadow: -4px -2px 4px 0 rgba(0, 0, 0, 0.16);
	height: 2.563rem;
	display: flex;
	padding: 11.4px 8px 0 8.5px;
`
const Content = styled.div`
	padding: 2.5rem 1.5rem 0 2rem;
`

export default ResponsiveLayout
